import {
  ACHIEVEMENT_COLORS,
  ACHIEVEMENT_ICONS,
  type AchievementColor,
  type AchievementIcon,
  ICON_RULES,
} from '@mogzo/shared';
import { type ChangeEvent, type FormEvent, useState } from 'react';
import { API_URL, ApiError, apiGet, apiSend, type Project } from '../lib/api';
import { boardIdFrom } from './BoardForm';
import { Alert, Button, cardClass, fieldClass } from './ui';

/** An achievement as the Achievements tab knows it. */
export interface AchievementSettings {
  id: string;
  name: string;
  description: string;
  points: number;
  hidden: boolean;
  steps: number | null;
  icon: AchievementIcon;
  iconUrl: string | null;
}

interface IconUpload {
  iconId: string;
  status: 'processing' | 'ready' | 'failed';
  error: string | null;
  url: string | null;
}

const COLOR_NAMES: Record<AchievementColor, string> = {
  yellow: 'Yellow',
  sky: 'Sky',
  pink: 'Pink',
  green: 'Green',
  violet: 'Violet',
  orange: 'Orange',
  grey: 'Grey',
  red: 'Red',
};

/** A MOGZO icon's picture, drawn by the API. */
export const mogzoIconUrl = (name: string, color: string) =>
  `${API_URL}/v1/achievement-icons/${name}.svg?color=${color}`;

function failure(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Could not reach the API. Try again.';
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Reads a picture's size in the browser, so the form can say what's wrong before uploading. */
async function sideOf(file: File): Promise<{ width: number; height: number } | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
  } catch {
    return null;
  }
}

/** The rules the API and worker check too, in the same words. */
async function pictureProblem(file: File): Promise<string | null> {
  if (!(ICON_RULES.types as readonly string[]).includes(file.type)) {
    return 'Use a PNG, WebP or JPEG picture.';
  }
  if (file.size > ICON_RULES.maxBytes) {
    const size =
      file.size >= 1024 * 1024
        ? `${(file.size / 1024 / 1024).toFixed(1)} MB`
        : `${Math.ceil(file.size / 1024)} KB`;
    return `That file is ${size}. Icons can be up to ${ICON_RULES.maxBytes / 1024} KB.`;
  }
  const side = await sideOf(file);
  if (!side) return 'That file isn’t a picture MOGZO can read. Use PNG, WebP or JPEG.';
  const { width, height } = side;
  if (width !== height) {
    return `That picture is ${width} × ${height}. Use a square one, ${ICON_RULES.minSide} to ${ICON_RULES.maxSide} pixels on each side.`;
  }
  if (width < ICON_RULES.minSide || width > ICON_RULES.maxSide) {
    return `That picture is ${width} × ${height}. Use one from ${ICON_RULES.minSide} to ${ICON_RULES.maxSide} pixels on each side.`;
  }
  return null;
}

async function base64Of(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

/**
 * Making or editing an achievement (screens 12–13 of the approved mockup): a MOGZO icon in a
 * colour, or the developer's own square picture. Test gets it right away.
 */
export function AchievementForm({
  project,
  achievement,
  onDone,
  onCancel,
}: {
  project: Project;
  achievement?: AchievementSettings;
  onDone: (id: string | null) => void;
  onCancel: () => void;
}) {
  const start = achievement?.icon;
  const [name, setName] = useState(achievement?.name ?? '');
  const [id, setId] = useState(achievement?.id ?? '');
  const [idEdited, setIdEdited] = useState(false);
  const [description, setDescription] = useState(achievement?.description ?? '');
  const [points, setPoints] = useState(String(achievement?.points ?? 10));
  const [steps, setSteps] = useState(achievement?.steps ? String(achievement.steps) : '');
  const [hidden, setHidden] = useState(achievement?.hidden ?? false);
  const [iconKind, setIconKind] = useState<'mogzo' | 'image'>(
    start?.kind === 'image' ? 'image' : 'mogzo',
  );
  const [iconName, setIconName] = useState(start?.kind === 'mogzo' ? start.name : 'trophy');
  const [color, setColor] = useState(start?.kind === 'mogzo' ? start.color : 'yellow');
  /** The own picture: the one saved, or one just uploaded and ready. */
  const [picture, setPicture] = useState<{ imageId: string; url: string } | null>(
    start?.kind === 'image' && achievement?.iconUrl
      ? { imageId: start.imageId, url: achievement.iconUrl }
      : null,
  );
  const [pictureNote, setPictureNote] = useState<string | null>(null);
  const [pictureError, setPictureError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const base = `/v1/dev/projects/${project.id}/achievements`;

  const icon: AchievementIcon | null =
    iconKind === 'mogzo'
      ? { kind: 'mogzo', name: iconName, color }
      : picture
        ? { kind: 'image', imageId: picture.imageId }
        : null;
  const previewUrl = iconKind === 'mogzo' ? mogzoIconUrl(iconName, color) : picture?.url;
  const shownPoints = Number(points) || 0;

  async function choosePicture(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setPictureError(null);
    setPictureNote(null);
    const problem = await pictureProblem(file);
    if (problem) return setPictureError(problem);
    setUploading(true);
    try {
      let upload = await apiSend<IconUpload>(
        'POST',
        `/v1/dev/projects/${project.id}/achievement-icons`,
        { contentType: file.type, data: await base64Of(file) },
      );
      // The worker checks and shrinks it, usually within a second or two.
      for (let tries = 0; upload.status === 'processing' && tries < 30; tries++) {
        await wait(1000);
        upload = await apiGet<IconUpload>(
          `/v1/dev/projects/${project.id}/achievement-icons/${upload.iconId}`,
        );
      }
      if (upload.status === 'ready' && upload.url) {
        setPicture({ imageId: upload.iconId, url: upload.url });
        setPictureNote(
          `${file.name} · ${Math.ceil(file.size / 1024)} KB · shown at ${ICON_RULES.side} × ${ICON_RULES.side}`,
        );
      } else {
        setPictureError(upload.error ?? 'That picture is taking too long. Try again.');
      }
    } catch (failed) {
      setPictureError(failure(failed));
    }
    setUploading(false);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!icon) return setError('Choose a picture for the icon, or use a MOGZO icon.');
    const value = Number(points);
    if (!Number.isInteger(value) || value < 0 || value > 100) {
      return setError('Points are a whole number from 0 to 100.');
    }
    const target = steps.trim() ? Number(steps.replace(/,/g, '')) : null;
    if (target !== null && (!Number.isInteger(target) || target < 2 || target > 1_000_000)) {
      return setError('Steps are a whole number from 2 to 1,000,000, or empty for none.');
    }
    const settings = {
      name: name.trim(),
      description: description.trim(),
      points: value,
      hidden,
      steps: target,
      icon,
    };
    setBusy(true);
    setError(null);
    try {
      if (achievement) await apiSend('PATCH', `${base}/${achievement.id}`, settings);
      else await apiSend('POST', base, { id, ...settings });
      onDone(achievement?.id ?? id);
    } catch (failed) {
      setError(failure(failed));
      setBusy(false);
    }
  }

  async function remove() {
    if (!achievement) return;
    setBusy(true);
    setError(null);
    try {
      await apiSend('DELETE', `${base}/${achievement.id}`);
      onDone(null);
    } catch (failed) {
      setError(failure(failed));
      setBusy(false);
    }
  }

  const choice = (selected: boolean) =>
    `flex min-h-11 items-center gap-2.5 rounded-[6px] border px-3.5 font-semibold ${
      selected ? 'border-brand bg-cream' : 'border-line bg-field'
    }`;

  return (
    <div className="flex flex-wrap items-start gap-5">
      <div className="flex min-w-0 flex-[999_1_520px] flex-col gap-5">
        <form
          onSubmit={save}
          aria-labelledby="achievement-form-heading"
          className={`${cardClass} flex flex-col gap-5 p-6`}
        >
          <div>
            <h2 id="achievement-form-heading" className="text-[22px] font-semibold">
              {achievement ? `Edit ${achievement.name}` : 'New achievement'}
            </h2>
            <p className="mt-1.5 text-muted">
              {achievement ? (
                <span className="font-mono">{achievement.id}</span>
              ) : (
                'Your test community gets it right away. Live players get it when you publish a version.'
              )}
            </p>
          </div>
          {error && <Alert tone="error">{error}</Alert>}

          <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-muted">
              Name players see
              <input
                value={name}
                maxLength={60}
                onChange={(event) => {
                  setName(event.target.value);
                  if (!achievement && !idEdited) setId(boardIdFrom(event.target.value));
                }}
                className={fieldClass}
              />
            </label>
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-muted">
              Id your game uses
              <input
                value={id}
                readOnly={Boolean(achievement)}
                onChange={(event) => {
                  setId(event.target.value);
                  setIdEdited(true);
                }}
                className={`${fieldClass} font-mono`}
              />
              <span className="font-medium">
                {achievement ? 'Fixed once made.' : 'Made from the name. Fixed once created.'}
              </span>
            </label>
          </div>

          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-muted">
            How to earn it (up to 200 characters)
            <textarea
              value={description}
              maxLength={200}
              rows={2}
              onChange={(event) => setDescription(event.target.value)}
              className={`${fieldClass} min-h-20 py-2.5`}
            />
          </label>

          <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-muted">
              Points
              <input
                value={points}
                inputMode="numeric"
                onChange={(event) => setPoints(event.target.value)}
                className={fieldClass}
              />
              <span className="font-medium">0 to 100.</span>
            </label>
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-muted">
              Steps (optional)
              <input
                value={steps}
                inputMode="numeric"
                placeholder="None"
                onChange={(event) => setSteps(event.target.value)}
                className={fieldClass}
              />
              <span className="font-medium">
                For “do it 100 times”: it unlocks when progress reaches this.
              </span>
            </label>
          </div>

          <label className={`${choice(hidden)} self-start`}>
            <input
              type="checkbox"
              checked={hidden}
              onChange={(event) => setHidden(event.target.checked)}
            />
            Hidden
            <span className="font-medium text-muted">
              · name and description stay secret until a player earns it
            </span>
          </label>

          <fieldset className="flex flex-col gap-3">
            <legend className="mb-2 text-[13px] font-semibold text-muted">Icon</legend>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2.5">
              <label className={choice(iconKind === 'mogzo')}>
                <input
                  type="radio"
                  name="icon-kind"
                  checked={iconKind === 'mogzo'}
                  onChange={() => setIconKind('mogzo')}
                />
                MOGZO icon
              </label>
              <label className={choice(iconKind === 'image')}>
                <input
                  type="radio"
                  name="icon-kind"
                  checked={iconKind === 'image'}
                  onChange={() => setIconKind('image')}
                />
                Your own picture
              </label>
            </div>

            {iconKind === 'mogzo' ? (
              <>
                <div
                  role="radiogroup"
                  aria-label="MOGZO icon"
                  className="grid grid-cols-[repeat(auto-fill,minmax(48px,1fr))] gap-2"
                >
                  {ACHIEVEMENT_ICONS.map((option) => (
                    // biome-ignore lint/a11y/useSemanticElements: a grid of picture choices
                    <button
                      key={option}
                      type="button"
                      role="radio"
                      aria-checked={option === iconName}
                      aria-label={option}
                      title={option}
                      onClick={() => setIconName(option)}
                      className={`rounded-[6px] border-2 p-0.5 ${
                        option === iconName ? 'border-ink' : 'border-transparent hover:border-line'
                      }`}
                    >
                      <img
                        src={mogzoIconUrl(option, color)}
                        alt=""
                        className="block aspect-square w-full rounded-[10px]"
                      />
                    </button>
                  ))}
                </div>
                <div role="radiogroup" aria-label="Colour" className="flex flex-wrap gap-2">
                  {(Object.keys(ACHIEVEMENT_COLORS) as AchievementColor[]).map((key) => (
                    // biome-ignore lint/a11y/useSemanticElements: colour swatches
                    <button
                      key={key}
                      type="button"
                      role="radio"
                      aria-checked={key === color}
                      aria-label={COLOR_NAMES[key]}
                      title={COLOR_NAMES[key]}
                      onClick={() => setColor(key)}
                      style={{ background: ACHIEVEMENT_COLORS[key] }}
                      className={`size-9 rounded-full border-2 ${
                        key === color ? 'border-ink' : 'border-line'
                      }`}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="flex flex-col gap-3">
                {picture && (
                  <div className="flex flex-wrap items-center gap-4">
                    <img
                      src={picture.url}
                      alt="Your icon"
                      className="size-24 rounded-[22px] border border-line"
                    />
                    <div className="flex flex-col gap-1 text-sm">
                      {pictureNote && <span className="text-muted">{pictureNote}</span>}
                      <strong className="text-success">Looks good</strong>
                    </div>
                  </div>
                )}
                {pictureError && <Alert tone="error">{pictureError}</Alert>}
                <div className="flex flex-wrap gap-2.5">
                  <label
                    className={`inline-flex min-h-11 cursor-pointer items-center rounded-full border border-line bg-card px-5 text-sm font-semibold hover:border-ink ${
                      uploading ? 'pointer-events-none opacity-50' : ''
                    }`}
                  >
                    {uploading ? 'Checking…' : picture ? 'Choose another' : 'Choose a picture'}
                    <input
                      type="file"
                      accept={ICON_RULES.types.join(',')}
                      onChange={choosePicture}
                      className="sr-only"
                    />
                  </label>
                  {picture && (
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setPicture(null);
                        setPictureNote(null);
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <p className="text-[13px] text-muted">
                  Square PNG, WebP or JPEG, {ICON_RULES.minSide} to {ICON_RULES.maxSide} pixels on
                  each side, up to {ICON_RULES.maxBytes / 1024} KB. MOGZO shrinks it to{' '}
                  {ICON_RULES.side} × {ICON_RULES.side} and removes camera details. Keep the
                  important part away from the corners: it’s shown with rounded corners.
                </p>
              </div>
            )}
          </fieldset>

          <div className="flex flex-wrap gap-2.5">
            <Button type="submit" disabled={busy || uploading}>
              {busy ? 'Saving…' : achievement ? 'Save changes' : 'Create achievement'}
            </Button>
            <Button variant="secondary" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
          </div>
        </form>

        {achievement && (
          <section
            aria-labelledby="delete-achievement-heading"
            className={`${cardClass} flex flex-col gap-3 border-danger px-6 py-5`}
          >
            <h2 id="delete-achievement-heading" className="text-[15px] font-semibold">
              Delete this achievement
            </h2>
            {confirmDelete ? (
              <div className="flex flex-col gap-2.5 rounded-[6px] bg-danger-soft px-4 py-3.5">
                <strong className="text-danger">Delete {achievement.name} from test?</strong>
                <p className="text-muted">
                  Unlocks of <span className="font-mono">{achievement.id}</span> are refused from
                  now on, so take it out of your game too. Testers’ progress on it is deleted. Live
                  players keep it until you publish a version without it.
                </p>
                <div className="flex flex-wrap gap-2.5">
                  <button
                    type="button"
                    onClick={remove}
                    disabled={busy}
                    className="mz-btn mz-btn-danger"
                  >
                    Yes, delete it
                  </button>
                  <Button
                    variant="secondary"
                    onClick={() => setConfirmDelete(false)}
                    disabled={busy}
                  >
                    Keep it
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                  Delete achievement
                </Button>
              </div>
            )}
          </section>
        )}
      </div>

      <aside className="flex max-w-[360px] flex-[1_1_280px] flex-col gap-5">
        <section
          aria-labelledby="achievement-preview-heading"
          className={`${cardClass} flex flex-col gap-3 p-5`}
        >
          <h2 id="achievement-preview-heading" className="text-[15px] font-semibold">
            How players see it
          </h2>
          <div className="flex items-center gap-3">
            {previewUrl ? (
              <img src={previewUrl} alt="" className="size-14 shrink-0 rounded-[14px]" />
            ) : (
              <span className="size-14 shrink-0 rounded-[14px] border border-dashed border-line" />
            )}
            <div className="min-w-0">
              <strong>{name.trim() || 'Achievement name'}</strong>
              <p className="text-[13px] text-muted">
                {description.trim() || 'How to earn it.'} · {shownPoints}{' '}
                {shownPoints === 1 ? 'point' : 'points'}
              </p>
            </div>
          </div>
          <p className="text-[13px] text-muted">
            The unlock pop-up over the game comes with the UI kit (M19).
          </p>
        </section>
        <section
          aria-labelledby="achievement-code-heading"
          className={`${cardClass} flex flex-col gap-3 p-5`}
        >
          <h2 id="achievement-code-heading" className="text-[15px] font-semibold">
            In your game
          </h2>
          <pre className="rounded-[6px] border border-line bg-field px-3.5 py-3 font-mono text-[13px] leading-relaxed whitespace-pre-wrap">
            {steps.trim()
              ? `await mogzo.achievements\n  .progress('${id || 'achievement-id'}', count);`
              : `await mogzo.achievements\n  .unlock('${id || 'achievement-id'}');`}
            {'\n\nconst all = await\n  mogzo.achievements.list();'}
          </pre>
        </section>
      </aside>
    </div>
  );
}
